import { useCallback, useEffect, useRef } from 'react';
import { draftKey } from '../utils/storageKeys';

const DEBOUNCE_MS = 800;

/**
 * Persists form data as the user types so nothing is lost on refresh.
 *
 * @param {string} route   - The FormComponent route key (e.g. 'salesTeam')
 * @param {string|number} id - Record id when editing; undefined/null for new records
 * @returns {{ saveDraft, loadDraft, clearDraft, storageKey }}
 */
export const useFormDraft = (route, id) => {
  const key = draftKey(route, id);
  const timer = useRef(null);

  // Debounced write — avoids hammering localStorage on every keystroke
  const saveDraft = useCallback(
    (data) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        try {
          localStorage.setItem(
            key,
            JSON.stringify({ data, savedAt: new Date().toISOString() })
          );
        } catch {
          // Storage full or unavailable — fail silently
        }
      }, DEBOUNCE_MS);
    },
    [key]
  );

  const loadDraft = useCallback(() => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw); // { data, savedAt }
    } catch {
      return null;
    }
  }, [key]);

  const clearDraft = useCallback(() => {
    clearTimeout(timer.current);
    localStorage.removeItem(key);
  }, [key]);

  // Cancel any pending debounced write on unmount
  useEffect(() => () => clearTimeout(timer.current), []);

  return { saveDraft, loadDraft, clearDraft, storageKey: key };
};
